import React from 'react';
import { Pencil1Icon, ReloadIcon } from '@radix-ui/react-icons';
import { ButtonIconType, ButtonSizeType, ButtonVariantType } from './button.types';
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    BadgeCheckIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronsLeftIcon,
    ChevronsRightIcon,
    CopyIcon,
    FilterIcon,
    MinusIcon,
    PlusIcon,
    TrashIcon,
    XIcon,
    Expand,
    Play,
    Pause,
    Upload,
    CalendarDays,
    ExternalLinkIcon,
} from 'lucide-react';

/* Base classes for button */
export const baseClasses = `
    disabled:opacity-50
    disabled:pointer-events-none
    focus-visible:outline-none
    focus-visible:ring-1
    focus-visible:ring-ring
    font-medium
    gap-2
    inline-flex
    items-center
    justify-center
    preflight
    rounded-md
    text-sm
    transition-colors
    whitespace-nowrap
`;

/*  Colors classes for button */
export const variantStyles = (variant: ButtonVariantType): string =>
    ({
        primary: `
            bg-primary
            hover:bg-primary/90
            shadow
            text-primary-foreground
        `,
        secondary: `
            bg-secondary
            hover:bg-secondary/80
            shadow-sm
            text-secondary-foreground
        `,
        destructive: `
            bg-destructive
            hover:bg-destructive/90
            shadow-sm
            text-destructive-foreground
        `,
        outline: `
            bg-background
            border
            border-input
            hover:bg-accent
            hover:text-accent-foreground
            shadow-sm
        `,
        ghost: `
            text-foreground
            hover:text-accent-foreground
        `,
        link: `
            hover:underline
            text-primary
            underline-offset-4
        `,
    })[variant];

/*  Size classes for button */
export const sizeStyles = (size: ButtonSizeType): string =>
    ({
        default: `
            h-9
            px-4
            py-2
        `,
        lg: `
            h-10
            rounded-md
            px-8
        `,
        sm: `
            h-8
            rounded-md
            px-3
            text-xs
        `,
    })[size];

export const iconSizeStyles = (size: ButtonSizeType): string =>
    ({
        default: `
            rounded-md !p-2.5 size-9
        `,
        lg: `
            h-10 rounded-md !p-3
        `,
        sm: `
            h-8 rounded-md !p-2
        `,
    })[size];

export const loadingStyles = (isLoading: boolean): string =>
    isLoading === true ? 'opacity-50 pointer-events-none' : '';

/* Set icons */
const iconMap: Record<ButtonIconType, React.ElementType> = {
    'arrow-left': ArrowLeftIcon,
    'arrow-right': ArrowRightIcon,
    'caret-left': ChevronLeftIcon,
    'caret-right': ChevronRightIcon,
    'chevrons-left': ChevronsLeftIcon,
    'chevrons-right': ChevronsRightIcon,
    badge: BadgeCheckIcon,
    copy: CopyIcon,
    filter: FilterIcon,
    minus: MinusIcon,
    pencil: Pencil1Icon,
    plus: PlusIcon,
    trash: TrashIcon,
    play: Play,
    pause: Pause,
    x: XIcon,
    expand: Expand,
    upload: Upload,
    calendar: CalendarDays,
    'external-link': ExternalLinkIcon,
};

export const iconComponent = (isLoading: boolean, icon?: ButtonIconType, iconClass?: string): React.ReactNode => {
    if (isLoading) {
        return <ReloadIcon className={iconClass} />;
    }

    if (icon === undefined) {
        return null;
    }

    const IconComponent = iconMap[icon];
    return <IconComponent className={iconClass} />;
};
